import { useEffect, useMemo, useState } from "react";
import Icon from "@mdi/react";
import {
  mdiDownload,
  mdiFileImportOutline,
  mdiPlus,
  mdiTrashCanOutline,
} from "@mdi/js";
import { useHistory, useLocation } from "react-router-dom";
import { fetchGet } from "../../../content_panel/services/serviceClient";
import Papa from "papaparse";
import { WarehouseModel } from "../../../../../erp/inventory/models/warehouse_model";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import { ItemModelV2 } from "../../../../../erp/inventory/models/item_model";
import {
  CreateRegularSTPayload,
  ItemRow,
  ItemWarehouseModel,
  areWarehousesInSameInternalGroup,
} from "../../model/warehouse_outward_model";
import WarehouseSearchField from "../../../../../erp/inventory/warehouse/warehouse_search_field";
import NewItemSearchByIdField from "../../../../../erp/inventory/items/item_search_by_ids";
import {
  CreateRegularStockTransfer,
  UpdatePendingHomeAppST,
  GetAvailableQty,
  GetItemWAC,
  BulkCheckItemAvailabilityApi,
} from "../../warehouse_module_repo";
import LoadingWidget from "../../../../../context_providers/modal/loader";
import RequiredMark from "../../../../new_components/common/required_mark";
import { validateWarehouseAddress } from "../../../../../utils/warehouse_util";

export default function RegularSTForm(): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const pendingId = new URLSearchParams(location.search).get("pendingId");

  const [fromWarehouse, setFromWarehouse] = useState<WarehouseModel | null>(
    null,
  );
  const [toWarehouse, setToWarehouse] = useState<WarehouseModel | null>(null);
  const [expectedDate, setExpectedDate] = useState<string>("");
  const { showToast } = useToast();
  const [items, setItems] = useState<ItemRow[]>([]);
  const [error, setError] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<ItemModelV2 | null>(null);
  const [purpose, setPurpose] = useState<string>("");
  const [contactName, setContactName] = useState<string>("");
  const [contactNumber, setContactNumber] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [logisticPartner, setLogisticPartner] = useState<string>("");
  const [trackingId, setTrackingId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkInput, setBulkInput] = useState("");
  const [unavailableItems, setUnavailableItems] = useState<
    { item_code: string; message: string }[]
  >([]);
  const [availableQtyMap, setAvailableQtyMap] = useState<
    Record<string, number>
  >({});

  const availableItemCodes = useMemo(() => {
    return Object.entries(availableQtyMap)
      .filter(([, qty]) => (qty ?? 0) > 0)
      .map(([itemCode]) => itemCode);
  }, [availableQtyMap]);

  // Internal ST is auto-detected from the warehouse pair (same building) so it
  // can't be missed by human error. The backend derives this authoritatively.
  const isInternal = areWarehousesInSameInternalGroup(
    fromWarehouse?.id,
    toWarehouse?.id,
  );

  useEffect(() => {
    if (!pendingId) setItems([]);
  }, [fromWarehouse?.id]);

  // When availableQtyMap loads after From Warehouse selection,
  // update availableQty on pre-filled items (Home App pending ST case)
  useEffect(() => {
    if (!pendingId || Object.keys(availableQtyMap).length === 0) return;
    setItems((prev) =>
      prev.map((item) => ({
        ...item,
        availableQty: availableQtyMap[item.itemCode] ?? 0,
      }))
    );
  }, [availableQtyMap]);

  // Pre-fill form when opened from a pending Home App ST
  useEffect(() => {
    if (!pendingId) return;
    const loadPendingST = async () => {
      try {
        const res = await fetchGet(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/st-inward-details/${pendingId}`
        );
        const st = res.data ?? res;
        if (!st) return;

        console.log("[Pre-fill] Pending ST data:", st);

        // Pre-fill To Warehouse — API pipeline returns it as st.toWarehouse
        if (st.toWarehouse?.id) {
          setToWarehouse(st.toWarehouse as WarehouseModel);
        } else if (st.to_warehouse_id) {
          setToWarehouse({ id: st.to_warehouse_id, name: st.to_warehouse_name } as WarehouseModel);
        }

        // Pre-fill contact name/number from pending ST
        if (st.contact_name) setContactName(st.contact_name);
        if (st.contact_number) setContactNumber(String(st.contact_number));

        // Pre-fill items from warehouse_stock_outward items array
        if (st.items?.length) {
          const prefilled: ItemRow[] = st.items.map((item: any) => ({
            itemCode: item.inventoryItemCode || "",
            itemName: item.itemName || "",
            quantity: item.requestedQty ?? 0,
            unitPrice: item.itemPrice ?? 0,
            isWacFetched: item.itemPrice > 0,
            itemUnit: item.itemUnit || "",
            itemHsn: item.itemHSN || "",
            subcategory: item.subCategory || "",
            availableQty: 0,
            requestedQty: item.requestedQty ?? 0,
          }));
          setItems(prefilled);
        }
      } catch (err) {
        console.error("Failed to load pending ST:", err);
      }
    };
    loadPendingST();
  }, [pendingId]);

  useEffect(() => {
    const fetchAllQty = async () => {
      if (!fromWarehouse?.id) return;

      try {
        setLoading(true);

        const res = await GetAvailableQty(fromWarehouse.id);

        if (!res?.success || !res?.data?.item_data) {
          setAvailableQtyMap({});
          return;
        }

        const map: Record<string, number> = {};

        res.data.item_data.forEach((item: any) => {
          map[item.item_code] = item.available_units ?? 0;
        });

        setAvailableQtyMap(map);
      } catch (err) {
        console.error(err);
        setAvailableQtyMap({});
      } finally {
        setLoading(false);
      }
    };

    fetchAllQty();
  }, [fromWarehouse?.id]); // 🔥 ONLY when warehouse changes

  const fetchAvailableQty = (itemId: string): number => {
    return availableQtyMap[itemId] ?? 0;
  };

  const handleFromWarehouseSelect = (warehouse: WarehouseModel | null) => {
    if (!warehouse) {
      setFromWarehouse(null);
      if (!pendingId) setToWarehouse(null);
      if (!pendingId) setItems([]);
      setSelectedItem(null);
      setBulkInput("");
      setUnavailableItems([]);
      setError("");
      return;
    }

    try {
      validateWarehouseAddress(warehouse.address!);
      setFromWarehouse(warehouse);
      if (!pendingId) setToWarehouse(null);
      if (!pendingId) setItems([]);
      setSelectedItem(null);
      setBulkInput("");
      setUnavailableItems([]);
      setError("");
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: err.message,
      });
      setFromWarehouse(null);
    }
  };

  const handleToWarehouseSelect = (warehouse: WarehouseModel | null) => {
    if (!warehouse) {
      setToWarehouse(null);
      return;
    }

    try {
      validateWarehouseAddress(warehouse.address!);
      setToWarehouse(warehouse);
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: err.message,
      });
      setToWarehouse(null);
    }
  };

  const fetchItemWac = async (
    itemCode: string,
  ): Promise<{ value: number; isFetched: boolean }> => {
    try {
      const res = await GetItemWAC(itemCode);

      if (res?.success && typeof res?.data?.wac_value === "number") {
        const value = res.data.wac_value;
        return { value, isFetched: value > 0 };
      }

      return { value: 0, isFetched: false };
    } catch {
      return { value: 0, isFetched: false };
    }
  };

  const addItem = async () => {
    if (!fromWarehouse) {
      showToast({
        type: ToastType.warning,
        text: "Select source warehouse first",
      });
      return;
    }

    if (!selectedItem) return;

    const lastItem = items[items.length - 1];
    if (lastItem && lastItem.quantity <= 0) {
      showToast({
        type: ToastType.warning,
        text: "Enter quantity for the previous item first",
      });
      return;
    }

    const exists = items.some((i) => i.itemCode === selectedItem.item_code);
    if (exists) {
      setError("Item already added");
      return;
    }

    // Check if item has HSN code
    if (
      !selectedItem.tax_detail?.hsn_sac ||
      selectedItem.tax_detail.hsn_sac.trim() === ""
    ) {
      showToast({
        type: ToastType.error,
        text: "Cannot add item without HSN code",
      });
      return;
    }

    const availableQty = fetchAvailableQty(selectedItem.item_code!);

    if (availableQty === 0) {
      showToast({
        type: ToastType.error,
        text: "This warehouse doesn't have this item to transfer",
      });
      return;
    }

    const wac = await fetchItemWac(selectedItem.item_code || "");
    if (!wac.isFetched) {
      showToast({
        type: ToastType.warning,
        text: "WAC not found. Enter unit price manually.",
      });
    }

    const newItem: ItemRow = {
      itemCode: selectedItem.item_code || "",
      itemName: selectedItem.name || "",
      quantity: 0,
      unitPrice: wac.value,
      isWacFetched: wac.isFetched,
      itemUnit: selectedItem.unit?.formal_name || "",
      itemHsn: selectedItem.tax_detail?.hsn_sac || "",
      subcategory: selectedItem.subcategory || "",
      availableQty,
    };

    setItems((prev) => [...prev, newItem]);
    setSelectedItem(null);
    setError("");
  };

  const removeItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleBulkAdd = async () => {
    setUnavailableItems([]);
    if (!fromWarehouse) {
      return showToast({
        type: ToastType.warning,
        text: "Select source warehouse first",
      });
    }

    if (!bulkInput.trim()) return;

    const lines = bulkInput.split("\n");
    const parsedItems: { item_code: string; quantity: number }[] = [];

    lines.forEach((line) => {
      const parts = line.split(/[,\t ]+/);
      if (parts.length >= 2) {
        const item_code = parts[0].trim();
        const quantity = Number(parts[1].trim());
        if (item_code && !isNaN(quantity) && quantity > 0) {
          parsedItems.push({ item_code, quantity });
        }
      }
    });

    if (parsedItems.length === 0) {
      return showToast({
        type: ToastType.warning,
        text: "Invalid format. Use: item_code, quantity",
      });
    }

    try {
      setLoading(true);
      const res = await BulkCheckItemAvailabilityApi(
        fromWarehouse.id,
        parsedItems,
      );
      if (res?.success) {
        const availableItems: ItemRow[] = res.available_items || [];
        const addableItems = availableItems.map((item) => ({
          ...item,
          isWacFetched: (item.unitPrice ?? 0) > 0,
        }));
        if (addableItems.some((item) => !item.isWacFetched)) {
          showToast({
            type: ToastType.warning,
            text: "Some items have WAC 0. Enter unit price manually before creating.",
          });
        }

        setItems((prev) => {
          const newItems = [...prev];
          addableItems.forEach((newItem) => {
            const exists = newItems.find(
              (i) => i.itemCode === newItem.itemCode,
            );
            if (!exists) {
              newItems.push(newItem);
            } else {
              exists.quantity += newItem.quantity;
            }
          });
          return newItems;
        });

        if (res.unavailable_items?.length > 0) {
          setUnavailableItems(res.unavailable_items);
          showToast({
            type: ToastType.warning,
            text: `Some items could not be added. Review errors below.`,
          });
        } else if (addableItems.length > 0) {
          showToast({
            type: ToastType.success,
            text: "Items added successfully",
          });
          setShowBulk(false);
        }
        setBulkInput("");
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to check availability",
        });
      }
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: err.message || "Bulk add failed",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUnavailableItems([]);
    if (!fromWarehouse) {
      return showToast({
        type: ToastType.warning,
        text: "Select source warehouse first",
      });
    }
    const file = e.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) =>
        header.trim().toLowerCase().replace(/_/g, " "),
      complete: async (results) => {
        const data = results.data as any[];
        const parsedItems: { item_code: string; quantity: number }[] = [];

        data.forEach((row) => {
          const item_code = row["item code"]?.toString().trim();
          const quantity = Number(row["quantity"]);
          if (item_code && !isNaN(quantity) && quantity > 0) {
            parsedItems.push({ item_code, quantity });
          }
        });

        if (parsedItems.length === 0) {
          showToast({
            type: ToastType.warning,
            text: "No valid items found in CSV. Headers should be 'item code, quantity'",
          });
          return;
        }

        try {
          setLoading(true);
          const res = await BulkCheckItemAvailabilityApi(
            fromWarehouse?.id || "",
            parsedItems,
          );
          if (res?.success) {
            const availableItems: ItemRow[] = res.available_items || [];
            const addableItems = availableItems.map((item) => ({
              ...item,
              isWacFetched: (item.unitPrice ?? 0) > 0,
            }));
            if (addableItems.some((item) => !item.isWacFetched)) {
              showToast({
                type: ToastType.warning,
                text: "Some items have WAC 0. Enter unit price manually before creating.",
              });
            }
            setItems((prev) => {
              const newItems = [...prev];
              addableItems.forEach((newItem) => {
                const exists = newItems.find(
                  (i) => i.itemCode === newItem.itemCode,
                );
                if (!exists) {
                  newItems.push(newItem);
                } else {
                  exists.quantity += newItem.quantity;
                }
              });
              return newItems;
            });

            if (res.unavailable_items?.length > 0) {
              setUnavailableItems(res.unavailable_items);
              showToast({
                type: ToastType.warning,
                text: `Some items could not be added. Review errors below.`,
              });
            } else if (addableItems.length > 0) {
              showToast({
                type: ToastType.success,
                text: "Items added from CSV successfully",
              });
              setShowBulk(false);
            }
          } else {
            showToast({
              type: ToastType.error,
              text: res?.error || "Failed to check availability",
            });
          }
        } catch (err: any) {
          showToast({
            type: ToastType.error,
            text: err.message || "Bulk add failed",
          });
        } finally {
          setLoading(false);
        }
      },
      error: (error) => {
        showToast({
          type: ToastType.error,
          text: "Error parsing CSV: " + error.message,
        });
      },
    });
  };

  const downloadSampleCSV = () => {
    const csvContent = "item code,quantity";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "sample_stock_transfer.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // const fetchAvailableQty = async (
  //   warehouseId: string,
  //   itemId: string,
  // ): Promise<number> => {
  //   try {
  //     const res = await GetAvailableQty(warehouseId);

  //     if (!res?.success || !res?.data?.item_data) return 0;
  //     console.log("result:", res);
  //     const match = res.data.item_data.find((i: any) => i.item_code === itemId);

  //     return match?.available_units ?? 0;
  //   } catch {
  //     return 0;
  //   }
  // };

  const calculateTotalValue = () => {
    return items.reduce(
      (total, item) => total + item.quantity * item.unitPrice,
      0,
    );
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    });
  };

  const handleCreate = async () => {
    if (!fromWarehouse) return setError("Please select source warehouse");
    if (!toWarehouse) return setError("Please select destination warehouse");
    if (!expectedDate) return setError("Please select expected date");
    if (!purpose.trim()) return setError("Please enter purpose");
    if (!description.trim()) return setError("Enter description");
    if (!contactName.trim()) return setError("Please enter contact name");

    if (!String(contactNumber).trim())
      return setError("Please enter contact number");

    if (String(contactNumber).replace(/\D/g, "").length < 10)
      return setError("Contact number must be at least 10 digits");
    if (items.length === 0) return setError("Add at least one item");
    if (items.some((i) => i.unitPrice <= 0))
      return setError("Item can't be added with wac value 0");

    if (fromWarehouse.id === toWarehouse.id)
      return setError("Source and destination warehouses cannot be the same");

    const hasEmpty = items.some(
      (i) => !i.itemCode.trim() || !i.itemName.trim(),
    );
    if (hasEmpty)
      return setError("Please fill all item details before creating");

    const hasInvalidQty = items.some((i) => i.quantity <= 0);
    if (hasInvalidQty)
      return setError("All items must have quantity greater than 0");

    // Check if all items have HSN codes
    const hasNoHsn = items.some((i) => !i.itemHsn || i.itemHsn.trim() === "");
    if (hasNoHsn)
      return setError("All items must have HSN codes before creating");

    setError("");

    const mappedItems: ItemWarehouseModel[] = items.map((item) => ({
      inventoryItemCode: item.itemCode,
      itemName: item.itemName,
      requestedQty: item.quantity,
      itemPrice: item.unitPrice,
      subCategory: item.subcategory,
      itemHSN: item.itemHsn,
      itemUnit: item.itemUnit,
      tax_detail: {
        igst: 0,
        cgst: 0,
        sgst: 0,
      },
      gst_applicable: false,
    }));

    const payload: CreateRegularSTPayload = {
      from_warehouse_id: fromWarehouse.id,
      from_warehouse_name: fromWarehouse.name,
      to_warehouse_id: toWarehouse.id,
      to_warehouse_name: toWarehouse.name,
      expected_date: new Date(expectedDate),
      items: mappedItems,
      description,
      purpose,
      contact_name: contactName.trim(),
      contact_number: String(contactNumber).trim(),
      is_internal: isInternal,
      shipping_details: isInternal
        ? undefined
        : {
            logistic_partner: logisticPartner.trim() || undefined,
            tracking_id: trackingId.trim() || undefined,
          },
    };

    try {
      setLoading(true);
      const res = pendingId
        ? await UpdatePendingHomeAppST(pendingId, payload)
        : await CreateRegularStockTransfer(payload);

      if (res?.success === false) {
        showToast({
          type: ToastType.error,
          text: res.error || "Failed to create stock transfer",
        });
        return;
      }

      showToast({
        type: ToastType.success,
        text: pendingId
          ? "Home App request confirmed and stock transfer created"
          : "Stock transfer request created successfully",
      });

      history.goBack();
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="warehouse-stock-transfer-regular-form">
      {/* BODY */}
      <div className="p-6 space-y-6">
        {/* Auto-detected internal transfer banner */}
        {isInternal && (
          <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 text-sm text-purple-800">
            <span className="font-semibold">This ST will be Internal.</span>{" "}
            Source and destination are in the same building — documents &
            logistics will be skipped.
          </div>
        )}

        {/* Warehouses */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">
              From Warehouse <RequiredMark />
            </label>
            <WarehouseSearchField
              id="warehouse-stock-transfer-regular-form-from-warehouse"
              value={fromWarehouse || undefined}
              placeholder="Select source"
              onSelect={handleFromWarehouseSelect}
            />
          </div>

          <div>
            <label className="text-sm font-medium">
              To Warehouse <RequiredMark />
              {pendingId && (
                <span className="ml-2 text-xs text-yellow-700 font-normal">(locked — from Home App request)</span>
              )}
            </label>
            <WarehouseSearchField
              id="warehouse-stock-transfer-regular-form-to-warehouse"
              disabled={!!pendingId || !fromWarehouse}
              value={toWarehouse || undefined}
              placeholder="Select destination"
              onSelect={handleToWarehouseSelect}
            />
          </div>
        </div>

        {/* Date + Purpose */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">
              Expected Date <RequiredMark />
            </label>
            <input
              id="warehouse-stock-transfer-regular-form-expected-date"
              type="date"
              value={expectedDate}
              onChange={(e) => setExpectedDate(e.target.value)}
              className="w-full h-8 border rounded-md px-3"
            />
          </div>

          <div>
            <label className="text-sm font-medium">
              Contact Name <RequiredMark />
            </label>
            <input
              id="warehouse-stock-transfer-regular-form-contact-name"
              type="text"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              className="w-full h-8 border rounded-md px-3"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">
              Contact Number <RequiredMark />
            </label>
            <input
              id="warehouse-stock-transfer-regular-form-contact-number"
              type="text"
              value={contactNumber}
              onChange={(e) => setContactNumber(e.target.value)}
              className="w-full h-8 border rounded-md px-3"
            />
          </div>

          <div>
            <label className="text-sm font-medium">
              Purpose <RequiredMark />
            </label>
            <input
              id="warehouse-stock-transfer-regular-form-purpose"
              type="text"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              className="w-full h-8 border rounded-md px-3"
            />
          </div>
        </div>
        {/* Description */}
        <div>
          <label className="text-sm font-medium">
            Description <RequiredMark />{" "}
          </label>
          <textarea
            id="warehouse-stock-transfer-regular-form-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={1}
            className="w-full border h-8 rounded-md px-3 py-2 resize-none"
          />
        </div>

        {!isInternal && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Logistic Partner</label>
              <input
                id="warehouse-stock-transfer-regular-form-logistic-partner"
                type="text"
                value={logisticPartner}
                onChange={(e) => setLogisticPartner(e.target.value)}
                className="w-full h-8 border rounded-md px-3"
                placeholder="Enter logistic partner"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Tracking ID</label>
              <input
                id="warehouse-stock-transfer-regular-form-tracking-id"
                type="text"
                value={trackingId}
                onChange={(e) => setTrackingId(e.target.value)}
                className="w-full h-8 border rounded-md px-3"
                placeholder="Enter tracking ID"
              />
            </div>
          </div>
        )}

        {/* Items */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Item Details</h2>
            <div className="flex justify-center gap-1 flex-col ">
              <button
                id="warehouse-stock-transfer-regular-form-toggle-bulk-btn"
                onClick={() => setShowBulk(!showBulk)}
                className="text-white text-sm font-medium hover:underline p-2 rounded-md transition-all bg-blue-500 hover:bg-blue-600"
              >
                {showBulk ? "Single Add" : "Bulk Add"}
              </button>
              <button
                id="warehouse-stock-transfer-regular-form-download-sample-csv-btn"
                onClick={downloadSampleCSV}
                className="text-gray-500 text-xs flex items-center gap-1 hover:text-blue-600 underline"
              >
                <Icon path={mdiDownload} size={0.5} />
                Download Sample CSV
              </button>
            </div>
          </div>

          {showBulk ? (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">
                Enter item code and quantity separated by comma or space (one
                item per line).
              </p>
              <textarea
                id="warehouse-stock-transfer-regular-form-bulk-input"
                value={bulkInput}
                onChange={(e) => setBulkInput(e.target.value)}
                placeholder="ITM-001, 10&#10;ITM-002, 5"
                rows={4}
                className="w-full border rounded-md px-3 py-2 text-sm"
              />

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  id="warehouse-stock-transfer-regular-form-bulk-add-btn"
                  onClick={handleBulkAdd}
                  disabled={loading || !bulkInput.trim()}
                  className="flex-1 bg-blue-600 text-white h-10 rounded-md font-medium disabled:opacity-50"
                >
                  {loading ? "Checking..." : "Add Manual Items"}
                </button>

                <div className="flex-1 relative">
                  <input
                    id="warehouse-stock-transfer-regular-form-csv-upload-input"
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={loading || !fromWarehouse}
                  />
                  <button
                    id="warehouse-stock-transfer-regular-form-csv-upload-btn"
                    disabled={loading || !fromWarehouse}
                    className="w-full border-2 border-blue-600 text-blue-600 h-10 rounded-md font-medium flex items-center justify-center gap-2 hover:bg-blue-50 transition-all disabled:opacity-50"
                  >
                    <Icon path={mdiFileImportOutline} size={0.7} />
                    Upload CSV
                  </button>
                </div>
              </div>

              {unavailableItems.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 space-y-2">
                  <p className="text-xs font-semibold text-red-700">
                    Failed to add some items:
                  </p>
                  <div className="max-h-32 overflow-y-auto">
                    <ul className="list-disc list-inside space-y-1">
                      {unavailableItems.map((item, id) => (
                        <li key={id} className="text-[10px] text-red-600">
                          <span className="font-medium">{item.item_code}</span>:{" "}
                          {item.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex gap-2">
              <div id="warehouse-stock-transfer-regular-form-item-search" className="flex-1">
                <NewItemSearchByIdField
                  disabled={!fromWarehouse}
                  value={selectedItem || ({} as ItemModelV2)}
                  placeholder="Search item"
                  onSelect={setSelectedItem}
                  item_codes={fromWarehouse ? availableItemCodes : undefined}
                />
              </div>
              <button
                id="warehouse-stock-transfer-regular-form-add-item-btn"
                onClick={addItem}
                disabled={loading}
                className={`px-4 bg-blue-600 text-white rounded-md flex items-center transition-all ${
                  loading
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-blue-700"
                }`}
              >
                <Icon path={mdiPlus} size={0.8} />
              </button>
            </div>
          )}

          <div className="space-y-3">
            {items.map((item, index) => (
              <div
                key={index}
                id={`warehouse-stock-transfer-regular-form-item-row-${item.itemCode ?? index}`}
                className="p-4 border rounded-xl bg-gray-50 space-y-3"
              >
                <div className="font-medium">
                  {item.itemName} - {item.itemCode}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1 ">
                    <label>
                      Quantity <RequiredMark />
                      {pendingId && item.requestedQty != null && (
                        <span className="ml-2 text-xs text-yellow-700 font-normal">
                          (max requested: {item.requestedQty})
                        </span>
                      )}
                    </label>
                    <input
                      id={`warehouse-stock-transfer-regular-form-item-row-${item.itemCode ?? index}-quantity`}
                      type="number"
                      min={1}
                      max={pendingId && item.requestedQty != null
                        ? Math.min(item.availableQty, item.requestedQty)
                        : item.availableQty}
                      value={item.quantity}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        const effectiveMax = pendingId && item.requestedQty != null
                          ? Math.min(item.availableQty, item.requestedQty)
                          : item.availableQty;
                        if (value > effectiveMax) return;
                        const updated = [...items];
                        updated[index].quantity = value;
                        setItems(updated);
                      }}
                      className="h-10 border rounded-md px-3"
                      placeholder="Quantity"
                    />
                  </div>

                  <div className="flex flex-col gap-1 ">
                    <label>
                      Unit Price <RequiredMark />
                    </label>
                    <input
                      id={`warehouse-stock-transfer-regular-form-item-row-${item.itemCode ?? index}-unit-price`}
                      type="number"
                      min={0}
                      value={item.unitPrice}
                      disabled={item.isWacFetched ?? item.unitPrice > 0}
                      onChange={(e) => {
                        const updated = [...items];
                        updated[index].unitPrice = Number(e.target.value);
                        setItems(updated);
                      }}
                      className="h-10 border rounded-md px-3"
                      placeholder="Unit Price"
                    />
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <p className="text-xs text-green-700 font-medium">
                    Available Units: {item.availableQty}
                  </p>

                  <button
                    id={`warehouse-stock-transfer-regular-form-item-row-${item.itemCode ?? index}-remove-btn`}
                    onClick={() => removeItem(index)}
                    disabled={loading}
                    className={`text-red-600 transition-all ${
                      loading
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:text-red-700"
                    }`}
                  >
                    <Icon path={mdiTrashCanOutline} size={0.8} />
                  </button>
                </div>
              </div>
            ))}

            {items.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-6">
                No items added
              </p>
            )}
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
            {error}
          </p>
        )}
      </div>

      <div className="border-t px-6 py-4 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div className="font-semibold">
          Total Value:{" "}
          <span className="text-blue-600">
            {formatCurrency(calculateTotalValue())}
          </span>
        </div>

        <div className="flex gap-3">
          <button
            id="warehouse-stock-transfer-regular-form-cancel-btn"
            onClick={() => history.goBack()}
            disabled={loading}
            className="border px-6 py-2 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            id="warehouse-stock-transfer-regular-form-submit-btn"
            onClick={handleCreate}
            disabled={loading}
            className={`bg-blue-600 text-white px-6 py-2 rounded-lg transition-all ${
              loading ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-700"
            }`}
          >
            {loading ? "Creating..." : "Create Stock Transfer"}
          </button>
        </div>
      </div>

      {loading && <LoadingWidget />}
    </div>
  );
}
